/**
 * useDJBreaks.js
 *
 * Extracted from PlayerContext.js - handles DJ segment/placard management,
 * preloading and playback of DJ breaks, show open, and ambient clip triggers.
 *
 * Provides: preloadDJBreak, playDJBreak, playShowOpen, setAvailableModules, setRadioStartTime,
 *           setProgress (counter-based break triggers + ambient logic)
 */
import { useCallback, useRef } from 'react';
import logger from '../services/logger';
import api from '../services/api';
import { selectModule, getTimeOfDay } from '../services/moduleSelector';
import { API_BASE_URL } from '../config';
import ambientAudioManager from '../services/ambientAudioManager';

const ActionTypes = {
  SET_PROGRESS: 'SET_PROGRESS',
  UPDATE_PLAYLIST: 'UPDATE_PLAYLIST'
};

// Check if an item is a segment (vs a track)
const isSegmentItem = (item) => item && item.type === 'segment';

export function useDJBreaks(state, dispatch, refs) {
  const {
    playerInstanceRef,
    segmentPlaybackRef,
    segmentCacheRef,
    playedSinceBreakRef,
    adLibFiredRef,
    hotMicFiredRef,
    producerFillScheduledRef,
    radioModeRef,
    fadeVolume,
    setCurrentTrackIndex,
    playlistTracksRef,
    currentTrackIndexRef,
  } = refs;


  // Available modules ref (populated by useRadioMode via radioContext)
  const availableModulesRef = useRef([]);
  const lastModuleIdRef = useRef(null);
  const radioStartTimeRef = useRef(null);

  // Per-track break trigger guards — reset on track change via clearTriggerFlags
  const preloadTriggeredRef = useRef(false);
  const playTriggeredRef = useRef(false);
  const trackCountedRef = useRef(false); // True once current track has been pushed to playedSinceBreakRef

  // Random threshold: fire a break after 3 or 4 tracks since last break
  const breakThresholdRef = useRef(3 + (crypto.getRandomValues(new Uint32Array(1))[0] % 2));

  // AbortController for in-flight preload fetch — aborted on skip/clear
  const abortControllerRef = useRef(null);
  // True once the 36s window entry has been logged this track — prevents 144x log spam
  const windowLoggedRef = useRef(false);
  // Whole-track SDK signal health score: 0–20 (fills on clean polls, drains on dirty)
  const sdkSignalCounterRef = useRef(0);
  // Previous poll's progressMs — used to compute delta for signal quality check
  const lastProgressMsRef = useRef(0);

  // Preload DJ break audio before track ends (called at ~30s remaining)
  const preloadDJBreak = useCallback(async (currentIdx, duration = 'medium') => {
    if (segmentCacheRef.current['pending']) {
      logger.debug(`[DJ-PRELOAD] ⚠️ Skipped — cache already has pending entry`);
      return;
    }

    logger.debug(`[DJ-PRELOAD] 🔄 Starting preload for idx=${currentIdx}`);
    segmentPlaybackRef.current = { ...segmentPlaybackRef.current, isPreloading: true, placardId: 'pending' };
    segmentCacheRef.current['pending'] = { loading: true };

    // Lock current + next track so playlist mutations can't shift them during generation
    const controlManager = radioModeRef.current?.services?.control;
    if (controlManager?.setTransitionLock) {
      controlManager.setTransitionLock(currentIdx, currentIdx + 1);
    }

    try {
      const tracks = refs.playlistTracksRef?.current ?? state.playlist.tracks;
      const nextTrack = tracks[currentIdx + 1];

      // Use accumulated buffer of actually-played tracks (immune to playlist mutations)
      const recentTracks = [...playedSinceBreakRef.current];

      const placardDuration = duration;

      // Select module using deterministic logic
      const sessionMinutes = radioStartTimeRef.current
        ? Math.floor((Date.now() - radioStartTimeRef.current) / 60000)
        : 0;

      const selectedModuleId = selectModule({
        timeOfDay: getTimeOfDay(),
        sessionMinutes,
        placardDuration,
        availableModules: availableModulesRef.current,
        lastModuleId: lastModuleIdRef.current
      });

      if (selectedModuleId) {
        logger.debug(`🎲 Module selected for placard: ${selectedModuleId}`);
        lastModuleIdRef.current = selectedModuleId;
      }

      const context = {
        recent_tracks: recentTracks,
        next_track: nextTrack ? {
          track_name: nextTrack.name,
          artist: typeof nextTrack.artists === 'string' ? nextTrack.artists : nextTrack.artists?.[0]?.name || 'Unknown'
        } : null,
        duration: placardDuration
      };

      const supabaseToken = await api.getSupabaseToken();
      const spotifyToken = localStorage.getItem('spotify_access_token');

      abortControllerRef.current = new AbortController();
      const response = await fetch(`${API_BASE_URL}/api/dj-commentary`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(spotifyToken && { 'Authorization': `Bearer ${spotifyToken}` }),
          ...(supabaseToken && { 'X-Supabase-Token': supabaseToken })
        },
        credentials: 'include',
        signal: abortControllerRef.current.signal,
        body: JSON.stringify({
          context,
          ...(selectedModuleId && { module_id: selectedModuleId })
        })
      });

      const data = await response.json();
      // Guard: if clearTriggerFlags fired while awaiting, controller is null — discard stale response
      if (!abortControllerRef.current) {
        logger.debug(`[DJ-PRELOAD] 🛑 Discarding stale response (track changed during response parse)`);
        return;
      }
      if (data.success && data.audio) {
        segmentCacheRef.current['pending'] = { audio: data.audio, script: data.script, trackIndex: currentIdx };
        logger.info(`[DJ-PRELOAD] ✅ Cache populated | module=${selectedModuleId || 'random'} | next="${context.next_track?.track_name || 'none'}"`);
      } else {
        logger.warn(`[DJ-PRELOAD] ❌ Response not usable | success=${data.success} | hasAudio=${!!data.audio}`);
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        logger.debug(`[DJ-PRELOAD] 🛑 Fetch aborted (track changed mid-preload)`);
        return;
      }
      logger.error('[DJ-PRELOAD] ❌ Preload failed:', error);
      delete segmentCacheRef.current['pending'];
      // Release lock on failure — no playback will follow
      if (controlManager?.clearTransitionLock) controlManager.clearTransitionLock();
    } finally {
      segmentPlaybackRef.current = { ...segmentPlaybackRef.current, isPreloading: false };
    }
  }, [state.playlist.tracks, segmentCacheRef, segmentPlaybackRef, playedSinceBreakRef, radioModeRef]);

  // Play DJ break - fade to silence, pause, play DJ, crossfade next track in
  const playDJBreak = useCallback(async (triggerTrackIndex) => {
    if (segmentPlaybackRef.current.isPlaying) {
      logger.debug(`[DJ-PLAY] ⚠️ Already playing — skipped`);
      return;
    }

    segmentPlaybackRef.current = { isPlaying: true, isPreloading: false, placardId: 'pending' };
    logger.info(`[DJ-PLAY] 🎙️ playDJBreak started | triggerIdx=${triggerTrackIndex}`);

    // Suppress ambient clips during DJ break
    ambientAudioManager.suppress();

    try {
      let audioBase64 = segmentCacheRef.current['pending']?.audio;
      logger.debug(`[DJ-PLAY] 📦 Cache check | hasAudio=${!!audioBase64}`);

      if (!audioBase64) {
        // Fallback: fetch now if not preloaded (use recent_tracks)
        logger.debug(`[DJ-PLAY] ⏳ No preloaded audio — falling back to live fetch`);
        const tracks = refs.playlistTracksRef?.current ?? state.playlist.tracks;
        const nextTrack = tracks[triggerTrackIndex + 1];
        const recentTracks = [...playedSinceBreakRef.current];

        const context = {
          recent_tracks: recentTracks,
          next_track: nextTrack ? {
            track_name: nextTrack.name,
            artist: typeof nextTrack.artists === 'string' ? nextTrack.artists : nextTrack.artists?.[0]?.name || 'Unknown'
          } : null,
          duration: 'medium'
        };

        const supabaseToken = await api.getSupabaseToken();
        const spotifyToken = localStorage.getItem('spotify_access_token');

        const response = await fetch(`${API_BASE_URL}/api/dj-commentary`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(spotifyToken && { 'Authorization': `Bearer ${spotifyToken}` }),
            ...(supabaseToken && { 'X-Supabase-Token': supabaseToken })
          },
          credentials: 'include',
          body: JSON.stringify({ context })
        });

        const data = await response.json();
        if (data.success && data.audio) {
          audioBase64 = data.audio;
        }
      }

      if (audioBase64) {
        // Phase 1: Fade to silence, then pause (dedicated break time)
        logger.debug(`[DJ-PLAY] 🔉 Phase 1: fading music to silence`);
        await fadeVolume(0, 2000);
        if (playerInstanceRef.current?.pause) {
          await playerInstanceRef.current.pause();
          logger.debug(`[DJ-PLAY] ⏸ Spotify paused`);
        }

        // Phase 2: Play DJ audio (full attention on DJ)
        const djAudio = new Audio(`data:audio/mpeg;base64,${audioBase64}`);
        djAudio.volume = 1.0;
        logger.debug(`[DJ-PLAY] 🎙️ Phase 2: playing DJ audio`);

        // Phase 3: Crossfade - condition-based, derived from actual speech duration
        let crossfadeTriggered = false;
        djAudio.addEventListener('loadedmetadata', () => {
          const djDurationSec = djAudio.duration;
          const overlapSec = Math.min(djDurationSec * 0.4, 10);
          const crossfadePoint = djDurationSec - overlapSec;
          const fadeDuration = overlapSec * 1000;
          logger.debug(`[DJ-PLAY] 🎵 Phase 3: DJ duration=${Math.round(djDurationSec)}s | crossfadePoint=${Math.round(crossfadePoint)}s | fadeDuration=${Math.round(fadeDuration)}ms`);

          djAudio.addEventListener('timeupdate', async () => {
            if (crossfadeTriggered || djAudio.currentTime < crossfadePoint) return;
            crossfadeTriggered = true;
            logger.debug(`[DJ-PLAY] 🔀 Crossfade: advancing to idx=${triggerTrackIndex + 1}`);
            if (playerInstanceRef.current?.setVolume) {
              await playerInstanceRef.current.setVolume(0);
            }
            setCurrentTrackIndex(triggerTrackIndex + 1);
            if (playerInstanceRef.current?.resume) {
              await playerInstanceRef.current.resume();
            }
            fadeVolume(1.0, fadeDuration);
          });
        });

        await djAudio.play();
        await new Promise(resolve => { djAudio.onended = resolve; });

        // Safety fallback: if crossfade didn't trigger (e.g. loadedmetadata never fired), force track advance
        if (!crossfadeTriggered) {
          logger.warn(`[DJ-PLAY] ⚠️ Crossfade did not trigger — forcing track advance to idx=${triggerTrackIndex + 1}`);
          setCurrentTrackIndex(triggerTrackIndex + 1);
          if (playerInstanceRef.current?.resume) {
            await playerInstanceRef.current.resume();
          }
        }

        // Phase 4: DJ ended - music should already be at or near full volume
        logger.debug(`[DJ-PLAY] ✅ Phase 4: DJ audio ended — restoring volume`);
        await fadeVolume(1.0, 500);
      } else {
        logger.warn(`[DJ-PLAY] ❌ No audio to play — break aborted`);
      }

      // Flush the played-tracks buffer (break just fired, start fresh)
      playedSinceBreakRef.current = [];
      const newThreshold = 3 + (crypto.getRandomValues(new Uint32Array(1))[0] % 2);
      breakThresholdRef.current = newThreshold;
      logger.debug(`[DJ-PLAY] 🔁 Buffer flushed | new threshold=${newThreshold}`);

      // Clear cache after use
      delete segmentCacheRef.current['pending'];

    } catch (error) {
      logger.debug(`[DJ-PLAY] 💥 Error during playback: ${error.message}`);
      logger.error('DJ Break playback failed:', error);
      // Ensure music resumes on error
      if (playerInstanceRef.current?.resume) {
        await playerInstanceRef.current.resume();
      }
      await fadeVolume(1.0, 500);
    } finally {
      segmentPlaybackRef.current = { isPlaying: false, isPreloading: false, placardId: null };
      ambientAudioManager.unsuppress();
      // Release playlist lock — mutations allowed again
      const ctrl = radioModeRef.current?.services?.control;
      if (ctrl?.clearTransitionLock) ctrl.clearTransitionLock();
    }
  }, [state.playlist.tracks, setCurrentTrackIndex, fadeVolume, playerInstanceRef, segmentPlaybackRef, segmentCacheRef, playedSinceBreakRef, radioModeRef]); // eslint-disable-line react-hooks/exhaustive-deps

  // Play show open - hybrid: pre-rendered welcome (instant) + generated song intro (seamless)
  const playShowOpen = useCallback(async () => {
    logger.platform('🎙️ Playing hybrid show open...');
    const player = playerInstanceRef.current;

    // Suppress ambient clips during show open
    ambientAudioManager.suppress();

    try {
      // Pause Spotify so DJ speaks before music starts
      if (player?.pause) await player.pause();

      // Build track context for the short song intro
      const currentTrack = state.playlist.tracks[state.currentTrackIndex];
      const trackContext = currentTrack && !isSegmentItem(currentTrack) ? {
        recent_tracks: [{
          track_name: currentTrack.name,
          artist: typeof currentTrack.artists === 'string' ? currentTrack.artists : currentTrack.artists?.[0]?.name || 'Unknown'
        }]
      } : null;

      const supabaseToken = await api.getSupabaseToken();
      const spotifyToken = localStorage.getItem('spotify_access_token');
      const headers = {
        'Content-Type': 'application/json',
        ...(spotifyToken && { 'Authorization': `Bearer ${spotifyToken}` }),
        ...(supabaseToken && { 'X-Supabase-Token': supabaseToken })
      };

      // 1. Fetch pre-rendered welcome clip from pool system (instant)
      const welcomePromise = fetch(`${API_BASE_URL}/api/dj-pool-clip?module_id=welcome`, {
        headers, credentials: 'include'
      }).then(r => r.json());

      // 2. Fire song intro generation in parallel
      const introPromise = trackContext ? fetch(`${API_BASE_URL}/api/dj-commentary`, {
        method: 'POST', headers, credentials: 'include',
        body: JSON.stringify({ module_id: 'song_intro', context: trackContext })
      }).then(r => r.json()) : Promise.resolve(null);

      // 3. Play pre-rendered welcome immediately
      const welcomeData = await welcomePromise;
      if (welcomeData?.success && welcomeData.audio) {
        const welcomeAudio = new Audio(`data:audio/mpeg;base64,${welcomeData.audio}`);
        welcomeAudio.volume = 1.0;
        await welcomeAudio.play();
        await new Promise(resolve => { welcomeAudio.onended = resolve; });
      }

      // 4. Play short song intro - music fades in underneath during the intro
      const introData = await introPromise;
      if (introData?.success && introData.audio) {
        const introAudio = new Audio(`data:audio/mpeg;base64,${introData.audio}`);
        introAudio.volume = 1.0;

        if (player?.setVolume) await player.setVolume(0);
        if (player?.resume) await player.resume();

        await introAudio.play();
        fadeVolume(0.2, 2500);
        await new Promise(resolve => { introAudio.onended = resolve; });
        await fadeVolume(1.0, 2000);
      } else {
        if (player?.setVolume) await player.setVolume(0);
        if (player?.resume) await player.resume();
        await fadeVolume(1.0, 1500);
      }

    } catch (error) {
      logger.error('Show open playback failed:', error);
      if (player?.resume) await player.resume();
      if (player?.setVolume) await player.setVolume(1.0);
    } finally {
      ambientAudioManager.unsuppress();
    }
  }, [state.playlist.tracks, state.currentTrackIndex, fadeVolume, playerInstanceRef]);

  // setProgress with ambient trigger logic and placard preload/play triggers
  const setProgress = useCallback((progressMs, durationMs) => {
    dispatch({ type: ActionTypes.SET_PROGRESS, payload: { progressMs, durationMs } });

    // === SDK SIGNAL HEALTH MONITOR ===
    const delta = progressMs - lastProgressMsRef.current;
    lastProgressMsRef.current = progressMs;
    const cleanSignal = (
      durationMs > 0 &&
      progressMs > 0 &&
      delta >= 0 &&
      delta < 2000 &&
      !window._playerStateFlags?.isSeeking &&
      !window._playerStateFlags?.isChangingTrack
    );
    if (cleanSignal) {
      if (sdkSignalCounterRef.current < 20) sdkSignalCounterRef.current += 1;
    } else {
      if (sdkSignalCounterRef.current > 0) sdkSignalCounterRef.current -= 1;
    }

    // Master abort guard — if preload is active and tank drops below 16, abort everything
    if (preloadTriggeredRef.current && sdkSignalCounterRef.current < 16) {
      logger.warn(`[SDK-HEALTH] ⚠️ Counter dropped below 16 — aborting | counter=${sdkSignalCounterRef.current}`);
      clearTriggerFlags(false); // health abort — track didn't change, preserve 80% count
      return;
    }

    // AI Radio timing integration
    if (radioModeRef.current && radioModeRef.current.handleProgressUpdate) {
      radioModeRef.current.handleProgressUpdate(progressMs, durationMs);
    }

    // Auto-queue: check if we need more tracks (lightweight ref check)
    if (refs.checkQueueRef?.current) {
      refs.checkQueueRef.current();
    }

    const timeRemaining = durationMs - progressMs;
    const currentIdx = state.currentTrackIndex;

    // === AMBIENT TRIGGERS (radio mode only) ===
    if (radioModeRef.current?.isActive) {
      // Ad lib: evaluate once per track in the 3-12s window
      if (progressMs >= 3000 && progressMs <= 12000 && !adLibFiredRef.current.has(currentIdx)) {
        adLibFiredRef.current.add(currentIdx);
        if (Math.random() < 0.05) {
          ambientAudioManager.playClip('ad_libs');
        }
      }

      // Hot mic: evaluate once per track just past the halfway mark
      const midPoint = durationMs * 0.52;
      if (progressMs >= midPoint && progressMs <= midPoint + 10000 && !hotMicFiredRef.current.has(currentIdx)) {
        hotMicFiredRef.current.add(currentIdx);
        if (!preloadTriggeredRef.current && Math.random() < 0.05) {
          ambientAudioManager.playClip('hot_mic');
        }
      }

      // Producer fill: schedule if DJ hasn't spoken in 4+ tracks and no break pending
      if (timeRemaining <= 15000 && timeRemaining > 10000 && !producerFillScheduledRef.current) {
        const tracksSinceBreak = playedSinceBreakRef.current.length;
        if (tracksSinceBreak >= 4 && !preloadTriggeredRef.current) {
          producerFillScheduledRef.current = true;
        }
      }
    }

    // Count current track toward break threshold at 80% listen mark
    if (!trackCountedRef.current && durationMs > 0 && progressMs / durationMs >= 0.8) {
      if (currentTrackIndexRef?.current !== currentIdx) return; // stale closure tick — skip
      const currentTrack = playlistTracksRef.current?.[currentIdx]; // always-current list
      if (currentTrack && currentTrack.type !== 'segment') {
        playedSinceBreakRef.current.push({
          track_name: currentTrack.name,
          artist: typeof currentTrack.artists === 'string'
            ? currentTrack.artists
            : currentTrack.artists?.[0]?.name || 'Unknown'
        });
        trackCountedRef.current = true; // only mark counted after successful push
        logger.debug(`[DJ-COUNTER] ✅ Track counted at 80%: "${currentTrack.name}" | buffer=${playedSinceBreakRef.current.length} | threshold=${breakThresholdRef.current}`);
      }
    }

    // Dynamic preload trigger — fires at (durationMs - 36000) so synthesis always gets 36s regardless of track length
    // Tracks shorter than 36s are skipped entirely — no synthesis window is possible
    const triggerPoint = durationMs - 36000;
    if (durationMs > 36000 && progressMs >= triggerPoint && !preloadTriggeredRef.current) {
      if (!windowLoggedRef.current) {
        windowLoggedRef.current = true;
        logger.debug(`[DJ-COUNTER] ⏱ Dynamic trigger point reached | idx=${currentIdx} | timeRemaining=${Math.round(timeRemaining/1000)}s | buffer=${playedSinceBreakRef.current.length} | threshold=${breakThresholdRef.current}`);
      }
      if (playedSinceBreakRef.current.length >= breakThresholdRef.current) {
        if (currentTrackIndexRef.current !== currentIdx) return; // stale tick guard — mirrors line 437
        preloadTriggeredRef.current = true;
        const placardDuration = sdkSignalCounterRef.current >= 20 ? 'long'
                              : sdkSignalCounterRef.current >= 18 ? 'medium'
                              : 'short'; // 16-17
        logger.debug(`[DJ-PRELOAD] 🚀 Triggered at ${Math.round(timeRemaining/1000)}s remaining | idx=${currentIdx} | buffer=${playedSinceBreakRef.current.length} tracks | signalScore=${sdkSignalCounterRef.current} | duration=${placardDuration}`);
        preloadDJBreak(currentIdx, placardDuration);
      }
    }

    if (timeRemaining <= 6000 && preloadTriggeredRef.current && !playTriggeredRef.current) {
      const cached = segmentCacheRef.current['pending'];
      if (cached?.audio && !segmentPlaybackRef.current.isPlaying) {
        if (cached.trackIndex !== undefined && cached.trackIndex !== currentTrackIndexRef.current) {
          logger.warn(`[DJ-PLAY] 🚫 Cache mismatch — cached idx=${cached.trackIndex} vs current=${currentTrackIndexRef.current} — discarding stale audio`);
          clearTriggerFlags();
          return;
        }
        playTriggeredRef.current = true;
        logger.info(`[DJ-PLAY] ▶️ Firing playDJBreak | idx=${currentTrackIndexRef.current} | signalScore=${sdkSignalCounterRef.current} | timeRemaining=${Math.round(timeRemaining/1000)}s`);
        playDJBreak(currentTrackIndexRef.current);
      } else {
        logger.debug(`[DJ-PLAY] 🔔 6s window | cacheReady=${!!cached?.audio} | isPlaying=${segmentPlaybackRef.current.isPlaying} | timeRemaining=${Math.round(timeRemaining/1000)}s | signalScore=${sdkSignalCounterRef.current}`);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.currentTrackIndex]);

  // Set available modules (called by useRadioMode after fetching from backend)
  const setAvailableModules = useCallback((modules) => {
    availableModulesRef.current = modules;
  }, []);

  // Set radio start time (called by useRadioMode on activation)
  const setRadioStartTime = useCallback((time) => {
    radioStartTimeRef.current = time;
  }, []);

  // Expose the preload/play triggered refs for clearing from setCurrentTrackIndex
  const clearTriggerFlags = useCallback((resetTrackCount = true) => {
    const hadAbort = !!abortControllerRef.current;
    const hadCache = !!segmentCacheRef.current['pending'];
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    preloadTriggeredRef.current = false;
    playTriggeredRef.current = false;
    if (resetTrackCount) trackCountedRef.current = false;
    windowLoggedRef.current = false;
    lastProgressMsRef.current = 0;
    delete segmentCacheRef.current['pending'];
    const ctrl = radioModeRef.current?.services?.control;
    if (ctrl?.clearTransitionLock) ctrl.clearTransitionLock();
    logger.debug(`[DJ-CLEAR] 🧹 clearTriggerFlags | abortFired=${hadAbort} | cacheCleared=${hadCache} | lockReleased=${!!ctrl?.clearTransitionLock}`);
  }, [segmentCacheRef, radioModeRef]);

  return {
    setProgress,
    preloadDJBreak,
    playDJBreak,
    playShowOpen,
    setAvailableModules,
    setRadioStartTime,
    clearTriggerFlags
  };
}
